import { groupBy } from '@toggle/helpers';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentData } from '~/stores/use-chat/assistant-response.type';

import * as S from './DocumentMessage.styles';

export interface DocumentMessageProps {
  data: DocumentData;
}

type ReplaceWithNode = {
  message: ReactNode[];
  document: {
    name: string;
    citations: number;
    url: string;
    title: string;
    pageNumber: number;
  }[];
};

export const DocumentMessage = ({ data }: DocumentMessageProps) => {
  const { t } = useTranslation('intel');
  const citations = data.citationsResult.citations;
  const replaceWithNode = (text: string) => {
    const docKeys = Object.keys(citations);
    const TEXT_IN_SQUARE_BRACKETS = new RegExp(
      '\\[' + docKeys.join('\\]|\\[') + '\\]',
      'g'
    );

    const stripSurroundingBrackets = (text: string) =>
      text.substring(1, text.length - 1);

    const matches = text
      .match(TEXT_IN_SQUARE_BRACKETS)
      ?.map(stripSurroundingBrackets);

    return text.split(TEXT_IN_SQUARE_BRACKETS).reduce(
      (a, c, i) => {
        if (matches && matches[i]) {
          const document = citations[matches[i]];
          const count = i + 1;

          return {
            document: [
              ...a.document,
              {
                name: matches[i],
                citations: count,
                ...document,
              },
            ],
            message: [
              ...a.message,
              c,
              <S.CitationLink
                key={c}
                data-testid={matches[i]}
                title=""
                href={`${document.url}#page=${document.pageNumber}`}
                target="_blank"
              >
                {count}
              </S.CitationLink>,
            ],
          };
        }
        return { document: a.document, message: [...a.message, c] };
      },
      { message: [], document: [] } as ReplaceWithNode
    );
  };
  const { message, document } = replaceWithNode(data.message);
  const result = groupBy(document, a => a.name);

  return (
    <>
      <p data-testid="doc-message">{message}</p>
      {!!Object.keys(citations).length && (
        <S.DocumentContainer data-testid="doc-container">
          <S.Evidence>{t('intel:document-message.evidence')}</S.Evidence>
          {Object.entries(result).map(([key, doc]) => {
            const { name, url, title, pageNumber } = doc[0];
            return (
              <S.DocumentLink
                key={key}
                data-testid={`doc-${name}`}
                href={`${url}#page=${pageNumber}`}
                target="_blank"
              >
                {doc.map(d => (
                  <S.Citation key={`key-${d.citations}`}>
                    {d.citations}
                  </S.Citation>
                ))}
                <S.PdfIcon />
                <span>
                  {t('intel:document-message.fileName', {
                    title,
                    pageNumber,
                  })}
                </span>
              </S.DocumentLink>
            );
          })}
        </S.DocumentContainer>
      )}
    </>
  );
};
